// Rehab Constants with Icons and Options for GQL Integration

import Compass from "phosphor-react-native/src/icons/CompassTool"
import Crane from "phosphor-react-native/src/icons/Crane"
import Frame from "phosphor-react-native/src/icons/FrameCorners"
import Roof from "phosphor-react-native/src/icons/SolarRoof"
import Shield from "phosphor-react-native/src/icons/ShieldCheckered"
import Grid from "phosphor-react-native/src/icons/GridFour"
import Pipe from "phosphor-react-native/src/icons/Pipe"
import Bulb from "phosphor-react-native/src/icons/LightbulbFilament"
import Wind from "phosphor-react-native/src/icons/Wind"
import Layout from "phosphor-react-native/src/icons/Layout"
import DiamondsFour from "phosphor-react-native/src/icons/DiamondsFour"
import Oven from "phosphor-react-native/src/icons/Oven"
import Shower from "phosphor-react-native/src/icons/Shower"
import Bathtub from "phosphor-react-native/src/icons/Bathtub"
import Lamp from "phosphor-react-native/src/icons/LampPendant"
import WashingMachine from "phosphor-react-native/src/icons/WashingMachine"
import Flower from "phosphor-react-native/src/icons/Flower"
import Garage from "phosphor-react-native/src/icons/Garage"
import Stairs from "phosphor-react-native/src/icons/Stairs"
import Warehouse from "phosphor-react-native/src/icons/Warehouse"
import House from "phosphor-react-native/src/icons/House"
import Wall from "phosphor-react-native/src/icons/Wall"
import Door from "phosphor-react-native/src/icons/Door"
import PaintBrushBroad from "phosphor-react-native/src/icons/PaintBrushBroad"
import Clipboard from "phosphor-react-native/src/icons/Clipboard"
import Piggy from "phosphor-react-native/src/icons/PiggyBank"
import { type DropdownOption } from "@/types/dropdownTypes"
import { toTitleCase } from "@/utils/StringUtils"

export interface RehabOption {
  modifier?: string // e.g., "Light," "Moderate," "Extensive" when needed
  detail: string // e.g., "Clean Out," "Major Clear Out, Removing Kitchen/Baths"
  requiresDetails?: boolean
  fieldName: string // the enum value to send to the BE
}

export interface RehabCategory {
  label: string
  longLabel?: string
  description?: string
  options: RehabOption[]
  icon?: React.ComponentType
  additionalInfoPlaceholder?: string
  fieldName: string
  selectType: "single" | "multi"
  tallModal?: boolean
  requiresDetails?: boolean
}

export const GENERIC_SELECT_TEXT = "Select..."

const STANDARD_MISC_DESCRIPTION =
  "Fill in the misc information and budget that’s needed for this category."

export const STANDARD_DESC_PLACEHOLDER_TEXT = "Description..."

// could live in the same file, or a utils file
export function getStandardModalDescription(isDesktop: boolean) {
  return isDesktop
    ? "Select the specific line item related to the category. You must select at least one or more."
    : "Select the specific line item related to the category.\nYou must select at least one or more."
}

export function getRehabCategories(
  isDesktop: boolean
): Record<string, RehabCategory> {
  const STANDARD_MODAL_DESCRIPTION = getStandardModalDescription(isDesktop)

  return {
    Permits: {
      label: "Permits, Planning",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { detail: "Permits", fieldName: "permits" },
        {
          detail: "Architectural Plans & Permits",
          fieldName: "architectural_plans",
        },
        {
          detail: "Design Plans/Consultation/Survey/Engineer Reports",
          fieldName: "other_plans_and_reports",
        },
      ],
      icon: Compass,
      fieldName: "planning",
      selectType: "multi",
      additionalInfoPlaceholder: STANDARD_DESC_PLACEHOLDER_TEXT,
      tallModal: true,
    },
    Demolition: {
      label: "Demolition",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Clean Out", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Major clear out, removing kitchen/baths",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "Structural, walls, roof, etc.",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Crane,
      fieldName: "demo",
      selectType: "single",
      tallModal: true,
    },
    Foundation: {
      label: "Foundation",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        {
          modifier: "Light",
          detail: "Repairs (cracks, reinforcing, wall anchors)",
          requiresDetails: true,
          fieldName: "light",
        },
        {
          modifier: "Moderate",
          detail: "New elements (footings, beams, underpinnings)",
          requiresDetails: true,
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new (pour new, addition work, new slab)",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Wall,
      fieldName: "foundation",
      selectType: "single",
      tallModal: true,
    },
    Framing: {
      label: "Framing",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Framing repairs", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair existing/new",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "Structural, addition work",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Frame,
      fieldName: "framing",
      selectType: "single",
      tallModal: true,
    },
    Roof: {
      label: "Roof/Gutters",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repairs", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Non-structural replacement",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "Structural",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Roof,
      fieldName: "roofing",
      selectType: "single",
      additionalInfoPlaceholder: STANDARD_DESC_PLACEHOLDER_TEXT,
      tallModal: true,
    },
    Exterior: {
      label: "Exterior/Siding/Exterior Paint",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair existing", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/new elements",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new, masonry, stucco, structural change",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Shield,
      fieldName: "exterior",
      selectType: "single",
      tallModal: true,
    },
    "Windows and Doors": {
      label: "Windows & Ext Doors",
      longLabel: "Windows & Exterior Doors",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        {
          modifier: "Light",
          detail: "Repair existing, misc trim/glass",
          fieldName: "light",
        },
        {
          modifier: "Moderate",
          detail: "Combo of repair and new windows",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "Majority of windows/doors new",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Grid,
      fieldName: "windows_doors",
      selectType: "single",
      tallModal: true,
    },
    Plumbing: {
      label: "Plumbing Rough",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair existing", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/replace",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "Full system replacement/additions",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Pipe,
      fieldName: "plumbing",
      selectType: "single",
      tallModal: true,
    },
    "Electric Rough": {
      label: "Electric Rough",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair Existing", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/replace",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "Full system replacement/additions",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Bulb,
      fieldName: "electrical",
      selectType: "single",
      tallModal: true,
    },
    HVAC: {
      label: "HVAC",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        {
          modifier: "Light",
          detail: "HVAC rough in - repair existing",
          fieldName: "light",
        },
        {
          modifier: "Moderate",
          detail: "HVAC rough in - combo of repair/replace",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "HVAC rough in - full system replacement/additions",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Wind,
      fieldName: "hvac",
      selectType: "single",
      tallModal: true,
    },
    "Insulation & Drywall": {
      label: "Insulation & Drywall",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair/patch", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/add new",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new replacement",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Layout,
      fieldName: "insulation",
      selectType: "single",
      tallModal: true,
    },
    "Interior Doors, Trim & Hardware": {
      label: "Int. Doors, Trim & Hardware",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair existing", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/add new",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new elements",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Door,
      fieldName: "interior_doors_trim",
      selectType: "single",
      tallModal: true,
    },
    "Interior Paint": {
      label: "Interior Paint",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair existing", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/add new",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new elements",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: PaintBrushBroad,
      fieldName: "interior_paint",
      selectType: "single",
      tallModal: true,
    },
    Flooring: {
      label: "Flooring",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair existing", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/add new",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new elements",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: DiamondsFour,
      fieldName: "flooring",
      selectType: "single",
      tallModal: true,
    },
    Kitchen: {
      label: "Kitchen",
      longLabel: "Kitchen - Cabinets/Countertops/Hardware",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        {
          modifier: "Light",
          detail: "Repair existing",
          requiresDetails: true,
          fieldName: "light",
        },
        {
          modifier: "Moderate",
          detail: "Combo of repair/add New",
          requiresDetails: true,
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new elements",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Oven,
      fieldName: "kitchen",
      selectType: "single",
      tallModal: true,
    },
    Bathroom: {
      label: "Bathrooms",
      longLabel: "Bathrooms - Vanities/Countertops/Showers",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        {
          modifier: "Light",
          detail: "Repair existing",
          requiresDetails: true,
          fieldName: "light",
        },
        {
          modifier: "Moderate",
          detail: "Combo of repair/all new",
          requiresDetails: true,
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new elements",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Bathtub,
      fieldName: "bathroom",
      selectType: "single",
      tallModal: true,
    },
    "Plumbing Finish": {
      label: "Plumbing Finish",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair existing", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/replace",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "Full system replacement/additions",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Shower,
      fieldName: "plumbing_finish",
      selectType: "single",
      tallModal: true,
    },
    "Electrical Finish": {
      label: "Electrical Finish",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair existing", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/replace",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new elements",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Lamp,
      fieldName: "electrical_finish",
      selectType: "single",
      tallModal: true,
    },

    Appliances: {
      label: "Appliances",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair existing", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/replace",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new elements",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: WashingMachine,
      fieldName: "appliances",
      selectType: "single",
      tallModal: true,
    },
    "Landscaping/Hardscaping": {
      label: "Landscaping/\nHardscaping/Deck",
      longLabel: "Landscape/\nHardscaping/Deck",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { detail: "Landscaping", fieldName: "landscaping" },
        { detail: "Hardscaping", fieldName: "hardscaping" },
        { detail: "Deck", fieldName: "deck" },
      ],
      icon: Flower,
      fieldName: "landscaping",
      selectType: "multi",
      additionalInfoPlaceholder: STANDARD_DESC_PLACEHOLDER_TEXT,
      tallModal: true,
    },
    "Garage/Driveway": {
      label: "Garage/Driveway",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        {
          modifier: "Light",
          detail: "Repair existing elements",
          fieldName: "light",
        },
        {
          modifier: "Moderate",
          detail: "Combo of repair/add New",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "Appliances all new elements",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Garage,
      fieldName: "garage_driveway",
      selectType: "single",
      tallModal: true,
    },
    Basement: {
      label: "Basement",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { modifier: "Light", detail: "Repair existing", fieldName: "light" },
        {
          modifier: "Moderate",
          detail: "Combo of repair/refinish",
          fieldName: "moderate",
        },
        {
          modifier: "Extensive",
          detail: "All new elements",
          requiresDetails: true,
          fieldName: "extensive",
        },
      ],
      icon: Stairs,
      fieldName: "basement",
      selectType: "single",
      tallModal: true,
    },
    Punchlist: {
      label: "Punchlist",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        { detail: "Post Reno Clean", fieldName: "clean" },
        { detail: "Minor Punchlist Work", fieldName: "minor" },
      ],
      icon: Clipboard,
      fieldName: "punch_list",
      selectType: "multi",
      additionalInfoPlaceholder: STANDARD_DESC_PLACEHOLDER_TEXT,
      tallModal: true,
    },
    ADU: {
      label: "ADUs",
      description: STANDARD_MODAL_DESCRIPTION,
      options: [
        {
          detail: "Existing ADU",
          requiresDetails: true,
          fieldName: "existing_adu",
        },
        {
          detail: "All new ADU",
          requiresDetails: true,
          fieldName: "all_new_adu",
        },
      ],
      icon: Warehouse,
      fieldName: "adu",
      selectType: "multi",
      tallModal: true,
      additionalInfoPlaceholder: STANDARD_DESC_PLACEHOLDER_TEXT,
    },
    Misc1: {
      label: "Misc 1",
      description: STANDARD_MISC_DESCRIPTION,
      options: [],
      icon: House,
      fieldName: "misc_1",
      selectType: "single",
      tallModal: true,
      additionalInfoPlaceholder:
        "The misc info can be typed in here with the info\nthat’s needed.",
      requiresDetails: true,
    },
    Misc2: {
      label: "Misc 2",
      description: STANDARD_MISC_DESCRIPTION,
      options: [],
      icon: House,
      fieldName: "misc_2",
      selectType: "single",
      tallModal: true,
      additionalInfoPlaceholder:
        "The misc info can be typed in here with the info\nthat’s needed.",
      requiresDetails: true,
    },
  }
}

export const CONTINGENCY_CATEGORY: Record<string, RehabCategory> = {
  Contingency: {
    label: "Contingency",
    description:
      "Let's build in some cushion. Select either 2%, 5%, or 10% that we'll add to your budget.",
    options: [
      { detail: "2%", fieldName: "0.02" },
      { detail: "5%", fieldName: "0.05" },
      { detail: "10%", fieldName: "0.10" },
    ],
    icon: Piggy,
    fieldName: "contingency",
    selectType: "single",
    tallModal: true,
  },
}

// todo: replace w/ codegened type
export interface GCContact {
  name: string
  phone: string
  email: string
  isSelfManaged: boolean
}

export const PROPERTY_TYPE_OPTIONS: Array<DropdownOption<string>> = [
  { label: "Single Family", value: "single_family" },
  { label: "Du/Tri/Quad Plex", value: "du_tri_quad_plex" },
  { label: "Multifamily (5+)", value: "multifamily" },
  { label: "Townhouse/Condo", value: "townhome_condo" },
  { label: "Apartment", value: "apartment" },
]

export const YES_NO_CHANGE_OPTIONS: Array<DropdownOption<boolean>> = [
  { label: "Yes", value: true },
  { label: "No", value: false },
]

export enum BudgetReviewStatus {
  INCOMPLETE = "incomplete",
  DRAFT = "draft",
  IN_REVIEW = "in_review",
  APPROVED = "approved",
  MORE_INFO_NEEDED = "more_info_needed",
  COMPLETE = "complete",
}

export const STATUS_COLOR_MAP: Record<string, string> = {
  [BudgetReviewStatus.INCOMPLETE]: "red-orange", // e.g., needs attention
  [BudgetReviewStatus.DRAFT]: "mustard-yellow", // similar treatment
  [BudgetReviewStatus.IN_REVIEW]: "salmon", // under review
  [BudgetReviewStatus.APPROVED]: "teal", // all good
  [BudgetReviewStatus.MORE_INFO_NEEDED]: "salmon", // warning state
  [BudgetReviewStatus.COMPLETE]: "dark-grey", // completed
}

export const getStatusColor = (status: string | null | undefined): string => {
  return (status && STATUS_COLOR_MAP[status]) || "red-orange"
}

export function getPropertyTypeLabel(propertyType: string | undefined): string {
  return (
    PROPERTY_TYPE_OPTIONS.find((option) => option.value === propertyType)
      ?.label ||
    propertyType ||
    "N/A"
  )
}

/**
 * Helper function to format the user's selected options for a category.
 *
 * It removes underscores, converts each word in each selection to Title Case,
 * and appends the category label. The selections are joined by a semicolon.
 *
 * For example, if the category label is "Foundation" and the user selects
 * "light_foundation" and "moderate_foundation", it will return:
 * "Light Foundation; Moderate  Foundation"
 *
 * If you don't want to duplicate the category label, you can modify the string
 * concatenation accordingly.
 *
 * @param category - The local category definition.
 * @param selections - The selected option(s) as a string or array.
 * @returns A formatted string.
 */

export function formatSelectedOptions(
  category: RehabCategory,
  selections: string | string[] | null
): string {
  if (!selections) return ""

  // Ensure we work with an array
  const selectionArray = Array.isArray(selections) ? selections : [selections]

  // Process each selection: remove underscores, convert to title case, and append category label.
  return selectionArray
    .map((s) => {
      const clean = s.replace(/_/g, " ") // remove underscores
      const titleCase = toTitleCase(clean) // convert each word to title case
      // Append the category label; if you prefer not to repeat the label, you can omit this.
      return `${titleCase} ${category.label}`.trim()
    })
    .join("; ")
}

export const getRehabTextForStatus = (
  status: BudgetReviewStatus | null
): string => {
  return status ? toTitleCase(status) : ""
}
